import { ref, reactive, type Ref } from "vue";
import {
  ScheduledDeliveryCreateInput,
  ScheduledDeliveryUpdateInput,
  DeliveryTimeListInput,
  DeliverableAreaDeliveryIdInput,
  OperationInput,
  ShopListInput,
} from "@/generated/graphql";
import {
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { GeneralTypeList } from "@/common/constant";
import type { ListData, DeliveryAreaType, DeliveryTimeResponseType } from "../types";

/**
 * 店舗間定期配達登録 フォーム状態管理
 * フォーム入力状態、選択肢変更ハンドラ、バリデーション、payload変換
 */
export const useScheduledDeliveryRegisterForm = (deps: {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
}) => {
  const { errdialog, errMessage } = deps;

  /**
   * 店舗間定期配達 input
   */
  const scheduledDeliveryCreateInput = reactive<ScheduledDeliveryCreateInput>({
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    shop_code: "",
    delivery_shop_code: "",
    area_code: "",
    delivery_id: 0,
    delivery_notes: undefined,
    delivery_request_create_date: 0,
    delivery_request_create_time: "",
    irregular_delivery_end_time: "",
    irregular_delivery_start_time: "",
    mon_to_deliver: 0,
    tue_to_deliver: 0,
    wed_to_deliver: 0,
    thu_to_deliver: 0,
    fri_to_deliver: 0,
    sat_to_deliver: 0,
    sun_to_deliver: 0,
    valid_date_from: undefined,
    valid_date_to: undefined,
    user_id: localStorageGetUserId(),
  });

  /**
   * 店舗間定期配達 input
   */
  const scheduledDeliveryUpdateInput =
    reactive<ScheduledDeliveryUpdateInput>({
      scheduled_delivery_no: "",
      chain_code: "",
      customer_code: localStorageGetCustomerCode(),
      shop_code: "",
      delivery_shop_code: "",
      area_code: "",
      delivery_id: 0,
      delivery_notes: undefined,
      delivery_request_create_date: 0,
      delivery_request_create_time: "",
      irregular_delivery_end_time: "",
      irregular_delivery_start_time: "",
      mon_to_deliver: 0,
      tue_to_deliver: 0,
      wed_to_deliver: 0,
      thu_to_deliver: 0,
      fri_to_deliver: 0,
      sat_to_deliver: 0,
      sun_to_deliver: 0,
      valid_date_from: undefined,
      valid_date_to: undefined,
      user_id: localStorageGetUserId(),
    });

  /**
   * 店舗一覧 input
   */
  const shopListInput = reactive<ShopListInput>({
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    limit: 10000,
    offset: 0,
  });

  /**
   * 配達時刻一覧 input
   */
  const deliveryTimeListInput = reactive<DeliveryTimeListInput>({
    area_code: [],
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    shop_code: "",
    limit: 10000,
    offset: 0,
  });

  /**
   * 配達可能なエリア・便 input
   */
  const deliverableAreaDeliveryIdInput =
    reactive<DeliverableAreaDeliveryIdInput>({
      chain_code: "",
      customer_code: localStorageGetCustomerCode(),
      shop_code: "",
      delivered_zip_code: "",
    });

  //  配達依頼作成 logInput
  const operationInput = reactive<OperationInput>({
    area_code: "10",
    detail: "操作詳細",
    display_category: "画面名",
    operation: "操作",
    user_id: localStorageGetUserId(),
  });

  /**
   * エリアリスト
   */
  const areaListItems = ref<DeliveryAreaType[] | null | undefined>([]);

  /**
   * 配達曜日情報
   */
  const deliveryIdSelectValues = ref<
    Array<{
      label: string;
      value: number;
    }> | null | undefined
  >(undefined);

  /**
   * 配達時間情報
   */
  const listDeliveryTime = ref<DeliveryTimeResponseType[] | null | undefined>();

  const deliverySelectValues = ref<
    | {
        area_delivery_id: string;
        area_code: string;
        delivery_id: number;
        delivery_name: string;
        delivery_start_time: string;
        delivery_end_time: string;
        delivery_time: string;
      }
    | null
    | undefined
  >(null);

  /**
   * 配達依頼作成日の選択ボックス
   */
  const deliveryRequestCreateDateItem = [
    { label: "当日", delivery_request_create_date: 0 },
    { label: "前日", delivery_request_create_date: 1 },
    { label: "前々日", delivery_request_create_date: 2 },
  ];

  const deliveryRequestCreateDateValues = ref<
    | { label: string; delivery_request_create_date: number }
    | null
    | undefined
  >({
    label: "当日",
    delivery_request_create_date: 0,
  });

  /**
   * 配達依頼作成時間の選択ボックス
   */
  const deliveryRequestCreateTimeItem = ref<Array<number>>([]);
  const deliveryRequestCreateTimeValues = ref<number | null | undefined>(0);

  // 配達便(便)データ
  const listDeliveryId = ref<ListData[]>([]);

  // 配達時間
  const delivery_time_input = ref<boolean>(true);

  /**
   * お届け便曜日選択時 @change
   */
  async function onChangeDeliveryIdSelect(): Promise<void> {
    if (
      deliveryIdSelectValues.value !== null &&
      deliveryIdSelectValues.value !== undefined &&
      deliveryIdSelectValues.value.length > 0
    ) {
      // 全選択
      deliveryIdSelectValues.value.forEach((value) => {
        if (value !== undefined && value !== null && value.value == 99) {
          deliveryIdSelectValues.value = [];
          deliveryIdSelectValues.value!.push(
            (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
              (element) => element.value === 1
            )!
          );
          deliveryIdSelectValues.value!.push(
            (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
              (element) => element.value === 2
            )!
          );
          deliveryIdSelectValues.value!.push(
            (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
              (element) => element.value === 3
            )!
          );
          deliveryIdSelectValues.value!.push(
            (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
              (element) => element.value === 4
            )!
          );
          deliveryIdSelectValues.value!.push(
            (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
              (element) => element.value === 5
            )!
          );
          deliveryIdSelectValues.value!.push(
            (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
              (element) => element.value === 6
            )!
          );
          deliveryIdSelectValues.value!.push(
            (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
              (element) => element.value === 7
            )!
          );
        }
      });

      // 表示用ソート（曜日順）
      deliveryIdSelectValues.value.sort(function (a, b) {
        return a.value - b.value;
      });
    }
  }

  /**
   * お届け便選択時 @change
   */
  async function onChangeDeliveryId(): Promise<void> {
    if (deliverySelectValues.value?.delivery_id == 99) {
      // 定期便
      deliveryRequestCreateTimeItem.value = [];

      delivery_time_input.value = true;
      scheduledDeliveryCreateInput.irregular_delivery_start_time = "";
      scheduledDeliveryCreateInput.irregular_delivery_end_time = "";
    } else {
      // 定期便以外
      delivery_time_input.value = false;

      scheduledDeliveryCreateInput.irregular_delivery_start_time =
        deliverySelectValues.value
          ? deliverySelectValues.value?.delivery_start_time.substring(0, 5)
          : "";
      scheduledDeliveryCreateInput.irregular_delivery_end_time =
        deliverySelectValues.value
          ? deliverySelectValues.value?.delivery_end_time.substring(0, 5)
          : "";
    }
    // 配達依頼作成時間作成
    await onChangeDeliveryRequestCreateDate();
  }

  /**
   * 配達開始時間変更時 @change
   */
  async function onChangeDeliveryStartTime(): Promise<void> {
    if (
      scheduledDeliveryCreateInput.irregular_delivery_start_time !== "" &&
      scheduledDeliveryCreateInput.irregular_delivery_end_time !== "" &&
      scheduledDeliveryCreateInput.irregular_delivery_start_time! >
        scheduledDeliveryCreateInput.irregular_delivery_end_time!
    ) {
      errdialog.value = true;
      errMessage.value =
        "配達時間の開始時間は、終了時間以前の時刻を入力してください。";
      scheduledDeliveryCreateInput.irregular_delivery_start_time = "";
      return;
    }

    // 配達依頼作成時間作成
    await onChangeDeliveryRequestCreateDate();
  }

  /**
   * 終了開始時間変更時 @change
   */
  async function onChangeDeliveryEndTime(): Promise<void> {
    if (
      scheduledDeliveryCreateInput.irregular_delivery_start_time !== "" &&
      scheduledDeliveryCreateInput.irregular_delivery_end_time !== "" &&
      scheduledDeliveryCreateInput.irregular_delivery_start_time! >
        scheduledDeliveryCreateInput.irregular_delivery_end_time!
    ) {
      errdialog.value = true;
      errMessage.value =
        "配達時間の終了時間は、開始時間以降の時刻を入力してください。";
      scheduledDeliveryCreateInput.irregular_delivery_end_time = "";
      return;
    }

    // 配達依頼作成時間作成
    await onChangeDeliveryRequestCreateDate();
  }

  /**
   * 配達依頼作成日変更 @change
   */
  async function onChangeDeliveryRequestCreateDate(): Promise<void> {
    scheduledDeliveryCreateInput.delivery_request_create_date =
      deliveryRequestCreateDateValues.value
        ? deliveryRequestCreateDateValues.value?.delivery_request_create_date
        : 0;

    deliveryRequestCreateTimeItem.value = [];

    //配達依頼作成時間の作成
    let endtime = 24;
    if (
      scheduledDeliveryCreateInput.delivery_request_create_date == 0 &&
      scheduledDeliveryCreateInput.irregular_delivery_start_time.length > 0
    ) {
      // 当日の場合は配達開始時間まで
      endtime = Number(
        scheduledDeliveryCreateInput?.irregular_delivery_start_time.substring(
          0,
          2
        )
      );
    }
    for (let i = 0; i < endtime; i++) {
      deliveryRequestCreateTimeItem.value.push(i);
    }

    if (
      Math.max.apply(null, deliveryRequestCreateTimeItem.value as number[]) <
      (deliveryRequestCreateTimeValues.value as number)
    ) {
      deliveryRequestCreateTimeValues.value = 0;
    }
  }

  /**
   * 定期配達有効期間開始日変更時 @change
   */
  async function onChangeValidDateFrom(): Promise<void> {
    scheduledDeliveryCreateInput.valid_date_from = formatDateInput(
      scheduledDeliveryCreateInput.valid_date_from
    );
    if (
      scheduledDeliveryCreateInput.valid_date_from !== "" &&
      scheduledDeliveryCreateInput.valid_date_to !== "" &&
      scheduledDeliveryCreateInput.valid_date_from! >
        scheduledDeliveryCreateInput.valid_date_to!
    ) {
      errdialog.value = true;
      errMessage.value =
        "定期配達有効期間の開始日は、終了日以前の日付を入力してください。";
      scheduledDeliveryCreateInput.valid_date_from = "";
      return;
    }
  }

  /**
   * 定期配達有効期間終了日変更時 @change
   */
  async function onChangeValidDateTo(): Promise<void> {
    scheduledDeliveryCreateInput.valid_date_to = formatDateInput(
      scheduledDeliveryCreateInput.valid_date_to
    );
    if (
      scheduledDeliveryCreateInput.valid_date_from !== "" &&
      scheduledDeliveryCreateInput.valid_date_to !== "" &&
      scheduledDeliveryCreateInput.valid_date_from! >
        scheduledDeliveryCreateInput.valid_date_to!
    ) {
      errdialog.value = true;
      errMessage.value =
        "定期配達有効期間の終了日は、開始日以降の日付を入力してください。";
      scheduledDeliveryCreateInput.valid_date_to = "";
      return;
    }
  }

  /**
   * お届け日を設定する
   */
  async function SetdeliveryId(): Promise<void> {
    deliveryIdSelectValues.value = [];
    if (scheduledDeliveryCreateInput.mon_to_deliver !== 0) {
      deliveryIdSelectValues.value!.push(
        (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
          (element) => element.value === 1
        )!
      );
    }
    if (scheduledDeliveryCreateInput.tue_to_deliver !== 0) {
      deliveryIdSelectValues.value!.push(
        (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
          (element) => element.value === 2
        )!
      );
    }
    if (scheduledDeliveryCreateInput.wed_to_deliver !== 0) {
      deliveryIdSelectValues.value!.push(
        (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
          (element) => element.value === 3
        )!
      );
    }
    if (scheduledDeliveryCreateInput.thu_to_deliver !== 0) {
      deliveryIdSelectValues.value!.push(
        (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
          (element) => element.value === 4
        )!
      );
    }
    if (scheduledDeliveryCreateInput.fri_to_deliver !== 0) {
      deliveryIdSelectValues.value!.push(
        (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
          (element) => element.value === 5
        )!
      );
    }
    if (scheduledDeliveryCreateInput.sat_to_deliver !== 0) {
      deliveryIdSelectValues.value!.push(
        (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
          (element) => element.value === 6
        )!
      );
    }
    if (scheduledDeliveryCreateInput.sun_to_deliver !== 0) {
      deliveryIdSelectValues.value!.push(
        (GeneralTypeList.week as Array<{ label: string; value: number }>).find(
          (element) => element.value === 7
        )!
      );
    }
  }

  /**
   * 休日設定を作成
   */
  async function createDeliveryId(): Promise<void> {
    // 初期化する
    scheduledDeliveryCreateInput.mon_to_deliver = 0;
    scheduledDeliveryCreateInput.tue_to_deliver = 0;
    scheduledDeliveryCreateInput.wed_to_deliver = 0;
    scheduledDeliveryCreateInput.thu_to_deliver = 0;
    scheduledDeliveryCreateInput.fri_to_deliver = 0;
    scheduledDeliveryCreateInput.sat_to_deliver = 0;
    scheduledDeliveryCreateInput.sun_to_deliver = 0;

    if (
      deliveryIdSelectValues.value !== undefined &&
      deliveryIdSelectValues.value !== null &&
      deliveryIdSelectValues.value.length > 0
    ) {
      for (const deliveryIdkey of deliveryIdSelectValues.value) {
        switch (String(deliveryIdkey.value)) {
          case "1":
            scheduledDeliveryCreateInput.mon_to_deliver = 1;
            break;
          case "2":
            scheduledDeliveryCreateInput.tue_to_deliver = 1;
            break;
          case "3":
            scheduledDeliveryCreateInput.wed_to_deliver = 1;
            break;
          case "4":
            scheduledDeliveryCreateInput.thu_to_deliver = 1;
            break;
          case "5":
            scheduledDeliveryCreateInput.fri_to_deliver = 1;
            break;
          case "6":
            scheduledDeliveryCreateInput.sat_to_deliver = 1;
            break;
          case "7":
            scheduledDeliveryCreateInput.sun_to_deliver = 1;
            break;
        }
      }
    }
  }

  /**
   * 登録 → 変更へデータセット
   */
  async function copyCreateToUpdate(): Promise<void> {
    scheduledDeliveryUpdateInput.chain_code =
      scheduledDeliveryCreateInput.chain_code;
    scheduledDeliveryUpdateInput.shop_code =
      scheduledDeliveryCreateInput.shop_code;
    scheduledDeliveryUpdateInput.delivery_shop_code =
      scheduledDeliveryCreateInput.delivery_shop_code;
    scheduledDeliveryUpdateInput.area_code =
      scheduledDeliveryCreateInput.area_code;
    scheduledDeliveryUpdateInput.delivery_id =
      scheduledDeliveryCreateInput.delivery_id;
    scheduledDeliveryUpdateInput.irregular_delivery_start_time =
      scheduledDeliveryCreateInput.irregular_delivery_start_time;
    scheduledDeliveryUpdateInput.irregular_delivery_end_time =
      scheduledDeliveryCreateInput.irregular_delivery_end_time;
    scheduledDeliveryUpdateInput.delivery_request_create_date =
      scheduledDeliveryCreateInput.delivery_request_create_date;
    scheduledDeliveryUpdateInput.delivery_request_create_time =
      scheduledDeliveryCreateInput.delivery_request_create_time;
    scheduledDeliveryUpdateInput.mon_to_deliver =
      scheduledDeliveryCreateInput.mon_to_deliver;
    scheduledDeliveryUpdateInput.tue_to_deliver =
      scheduledDeliveryCreateInput.tue_to_deliver;
    scheduledDeliveryUpdateInput.wed_to_deliver =
      scheduledDeliveryCreateInput.wed_to_deliver;
    scheduledDeliveryUpdateInput.thu_to_deliver =
      scheduledDeliveryCreateInput.thu_to_deliver;
    scheduledDeliveryUpdateInput.fri_to_deliver =
      scheduledDeliveryCreateInput.fri_to_deliver;
    scheduledDeliveryUpdateInput.sat_to_deliver =
      scheduledDeliveryCreateInput.sat_to_deliver;
    scheduledDeliveryUpdateInput.sun_to_deliver =
      scheduledDeliveryCreateInput.sun_to_deliver;
    scheduledDeliveryUpdateInput.valid_date_from =
      scheduledDeliveryCreateInput.valid_date_from;
    scheduledDeliveryUpdateInput.valid_date_to =
      scheduledDeliveryCreateInput.valid_date_to;
    scheduledDeliveryUpdateInput.delivery_notes =
      scheduledDeliveryCreateInput.delivery_notes;
  }

  /**
   * 入力項目をクリアする
   */
  async function allClear(): Promise<void> {
    // 登録用input
    scheduledDeliveryCreateInput.chain_code = "";
    scheduledDeliveryCreateInput.shop_code = "";
    scheduledDeliveryCreateInput.delivery_shop_code = "";
    scheduledDeliveryCreateInput.area_code = "";
    scheduledDeliveryCreateInput.delivery_id = 99;
    scheduledDeliveryCreateInput.irregular_delivery_start_time = "";
    scheduledDeliveryCreateInput.irregular_delivery_end_time = "";
    scheduledDeliveryCreateInput.delivery_request_create_date = 0;
    scheduledDeliveryCreateInput.delivery_request_create_time = "";
    scheduledDeliveryCreateInput.mon_to_deliver = 0;
    scheduledDeliveryCreateInput.tue_to_deliver = 0;
    scheduledDeliveryCreateInput.wed_to_deliver = 0;
    scheduledDeliveryCreateInput.thu_to_deliver = 0;
    scheduledDeliveryCreateInput.fri_to_deliver = 0;
    scheduledDeliveryCreateInput.sat_to_deliver = 0;
    scheduledDeliveryCreateInput.sun_to_deliver = 0;
    scheduledDeliveryCreateInput.valid_date_from = undefined;
    scheduledDeliveryCreateInput.valid_date_to = undefined;
    scheduledDeliveryCreateInput.delivery_notes = undefined;

    // 変更用input
    scheduledDeliveryUpdateInput.scheduled_delivery_no = "";
    scheduledDeliveryUpdateInput.chain_code = "";
    scheduledDeliveryUpdateInput.shop_code = "";
    scheduledDeliveryUpdateInput.delivery_shop_code = "";
    scheduledDeliveryUpdateInput.area_code = "";
    scheduledDeliveryUpdateInput.delivery_id = 99;
    scheduledDeliveryUpdateInput.irregular_delivery_start_time = "";
    scheduledDeliveryUpdateInput.irregular_delivery_end_time = "";
    scheduledDeliveryUpdateInput.delivery_request_create_date = 0;
    scheduledDeliveryUpdateInput.delivery_request_create_time = "";
    scheduledDeliveryUpdateInput.mon_to_deliver = 0;
    scheduledDeliveryUpdateInput.tue_to_deliver = 0;
    scheduledDeliveryUpdateInput.wed_to_deliver = 0;
    scheduledDeliveryUpdateInput.thu_to_deliver = 0;
    scheduledDeliveryUpdateInput.fri_to_deliver = 0;
    scheduledDeliveryUpdateInput.sat_to_deliver = 0;
    scheduledDeliveryUpdateInput.sun_to_deliver = 0;
    scheduledDeliveryUpdateInput.valid_date_from = undefined;
    scheduledDeliveryUpdateInput.valid_date_to = undefined;
    scheduledDeliveryUpdateInput.delivery_notes = undefined;

    deliveryIdSelectValues.value = [];
    deliverySelectValues.value = null;
    deliveryRequestCreateDateValues.value = {
      label: "当日",
      delivery_request_create_date: 0,
    };
  }

  function formatDateInput(value: string | Date | null | undefined): string {
    if (!value) return "";
    if (typeof value === "string") return value;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return {
    scheduledDeliveryCreateInput,
    scheduledDeliveryUpdateInput,
    shopListInput,
    deliveryTimeListInput,
    deliverableAreaDeliveryIdInput,
    operationInput,
    areaListItems,
    deliveryIdSelectValues,
    listDeliveryTime,
    deliverySelectValues,
    deliveryRequestCreateDateItem,
    deliveryRequestCreateDateValues,
    deliveryRequestCreateTimeItem,
    deliveryRequestCreateTimeValues,
    listDeliveryId,
    delivery_time_input,
    onChangeDeliveryIdSelect,
    onChangeDeliveryId,
    onChangeDeliveryStartTime,
    onChangeDeliveryEndTime,
    onChangeDeliveryRequestCreateDate,
    onChangeValidDateFrom,
    onChangeValidDateTo,
    SetdeliveryId,
    createDeliveryId,
    copyCreateToUpdate,
    allClear,
    formatDateInput,
  };
};
